import { createSelector, type MemoizedSelector } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { selectData } from '@ps/core-data-access';
import {
  audienceChildLocale,
  type ChildLocale,
  type Survey,
  mapSurveyLocalization,
  SURVEY_WORKFLOW_PAGE_DATA_KEY,
  toSurveyWorkflowPage,
} from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';
import {
  participatingSurveys,
  resolveActiveCoreChildKey,
} from './utils/core-children.helper';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyChildRegistryState } from '../survey-creation-state.model';
import { Nullable } from '@ps/utils';

const { selectCore } = surveyCreationFeature;

export const selectRootSurveyId = createSelector(
  selectCore,
  (state) => state.rootSurveyId,
);

// The row the route is about; unlike `selectSurvey` it does not follow the
// active child, so it stays put while a project's locale is switched.
export const selectRootSurvey = createSelector(selectCore, (state) =>
  state.rootSurveyId
    ? (state.children[state.rootSurveyId]?.survey ?? null)
    : null,
);

export const selectRootSurveyError = createSelector(selectCore, (state) =>
  state.rootSurveyId
    ? (state.children[state.rootSurveyId]?.error ?? null)
    : null,
);

export const selectActiveChildKey = createSelector(
  selectCore,
  (state) => state.activeChildKey,
);

export const selectKind = createSelector(selectCore, (state) => state.kind);

export const selectTrackerConfig = createSelector(
  selectCore,
  (state) => state.trackerConfig,
);

export const selectCoreChildren = createSelector(
  selectCore,
  (state) => state.children,
);

export const selectParticipatingSurveys = createSelector(
  selectCoreChildren,
  participatingSurveys,
);

// Only locales actually participating. participant is null (not false) for a standard survey
// or lone pre-draft locale — "no family" — and still counts as participating; only an explicit
// false (opted out) excludes it.
export const selectParticipantLocales = createSelector(
  selectCoreChildren,
  surveyCreationFeature.selectAudience,
  (children, audience): ReadonlySet<string> =>
    new Set(
      Object.entries(children)
        .filter(([, child]) => child.participant !== false)
        .map(([key, child]) =>
          Option.from<ChildLocale>(child.survey)
            .orElse(() => audienceChildLocale(audience.children[key]))
            .mapOr(key, (locale) =>
              mapSurveyLocalization(
                locale.countryCode,
                locale.languageShortCode,
              ),
            ),
        ),
    ),
);

type CoreChildSelector = MemoizedSelector<object, SurveyChildRegistryState>;

const selectActiveCoreChild: CoreChildSelector = createSelector(
  selectCore,
  (state) => {
    const key = resolveActiveCoreChildKey(state);
    return state.children[key] ?? DEFAULT_CORE_CHILD_STATE;
  },
);

// Any child by key, for work that runs on a child other than the active one.
const selectCoreChildFor = (key: SurveyChildKey): CoreChildSelector =>
  createSelector(
    selectCore,
    (state) => state.children[key] ?? DEFAULT_CORE_CHILD_STATE,
  );

// Shared by the active child and any child by key.
const surveyOf = (
  child: CoreChildSelector,
): MemoizedSelector<object, Nullable<Survey>> =>
  createSelector(child, ({ survey }) => survey);

export const selectSurvey = surveyOf(selectActiveCoreChild);

export const selectSurveyId = createSelector(
  selectSurvey,
  (survey) => survey?.id ?? null,
);

// Which workflow page is open, read off the route's own `data` — never stored
// in this feature, so nothing has to keep a copy in sync.
export const selectSurveyWorkflowPage = createSelector(selectData, (data) =>
  toSurveyWorkflowPage(data[SURVEY_WORKFLOW_PAGE_DATA_KEY]),
);

export const selectIsSurveyLoaded = createSelector(
  selectSurvey,
  (survey) => !!survey,
);

export const selectError = createSelector(
  selectActiveCoreChild,
  ({ error }) => error,
);

// Per child — the same projection over a child by key.
export const selectSurveyFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, Nullable<Survey>> =>
  surveyOf(selectCoreChildFor(key));
